/** One participant's published presence. Lives in Yjs Awareness — ephemeral,
 *  never in the Y.Doc (cursors must not persist, count as edits, or pollute
 *  undo/the S3 snapshot). Cursor + viewport are in CANVAS coords; each client
 *  maps them through its own (per-user local) viewport. */
export interface PresencePeer {
  /** Canonical identity (email/sub) — drives the deterministic color. */
  id: string;
  /** Display handle (email local-part or name). */
  name: string;
  /** `oklch(...)` identity color (== colorFor(id)). */
  color: string;
  initials: string;
  /** True for the agent (Viv) pseudo-peer. */
  isAgent: boolean;
  cursor?: { x: number; y: number };
  /** Frame id the peer currently has selected. */
  selection?: string;
  viewport?: { pan: { x: number; y: number }; zoom: number };
}

/** The per-client Awareness state we publish. A client carries its own
 *  `presence`, and — while it's running an agent turn — also the agent's. */
export interface AwarenessLocalState {
  presence?: PresencePeer;
  agent?: PresencePeer;
}

/**
 * Flatten an Awareness states map into the list of OTHER peers to render.
 * - own `presence` is skipped (you don't render your own cursor);
 * - `agent` is included from every client, INCLUDING self — the host running
 *   Viv's turn should see Viv too. Only the host sets `agent`, so there's no
 *   duplication across clients.
 */
export function peersFromStates(
  states: Map<number, AwarenessLocalState>,
  selfClientId: number,
): PresencePeer[] {
  const out: PresencePeer[] = [];
  for (const [cid, st] of states) {
    if (st?.agent?.id) out.push(st.agent);
    if (cid === selfClientId) continue;
    if (st?.presence?.id) out.push(st.presence);
  }
  return out;
}
